import { theme } from 'antd';
import { CalendarIcon, DeletedFilled, DisableIcon, EnableIcon, KeyIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { CreateBookingModal, DeletePeopleModal, DisablePeopleModal, EnablePeopleModal } from 'zComponents/molecules';
import { Button, Divider, Dropdown, Flex, Tooltip } from 'zui/Antd';

const ManageLockerPersonDropdown = ({ children, personID, isTransparent = false }) => {
	// ✅ Hooks
	const {
		token: {
			colorBgContainer,
			boxShadow,
			borderRadiusLG,
			paddingSM,
			fontSizeLG,
			colorPrimaryText,
			fontWeightStrong
		}
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Redux States
	const person = useSelector((state) => selectPeopleByPersonID(state, personID));
	const { loadingResetPassword } = useSelector((state) => state.People);

	// ✅ Local State
	const [openDropdown, setOpenDropdown] = useState(false);

	//✅ Permissions
	const canAddBooking = useManagePermittedRoles('addBooking');
	const canEnablePerson = useManagePermittedRoles('enablePerson');
	const canDisablePerson = useManagePermittedRoles('disablePerson');
	const canDeletePerson = useManagePermittedRoles('deletePerson');
	const canResetPasswordPerson = useManagePermittedRoles('resetPasswordPerson');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const items = [
		{
			key: 'Create Booking',
			label: (
				<CreateBookingModal personID={personID}>
					<Button
						type='text'
						icon={<CalendarIcon color='currentColor' />}
						block
						disabled={!canAddBooking}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Create Booking
					</Button>
				</CreateBookingModal>
			)
		},

		{
			key: 'Send Password Reset',
			label: (
				<Tooltip
					title={!canResetPasswordPerson ? permissionDeniedMessage : "Reset this Person's Password"}
					placement='top'
				>
					<Button
						type='text'
						icon={<KeyIcon color='currentColor' />}
						block
						onClick={(e) => {
							e.stopPropagation();
							if (!canResetPasswordPerson) return;
							dispatch(
								peopleActions.resetPersonPassword({
									email: person.email
								})
							);
							setOpenDropdown(false);
						}}
						loading={loadingResetPassword}
						disabled={!canResetPasswordPerson}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Send Password Reset
					</Button>
				</Tooltip>
			)
		},

		{
			key: '',
			label: <Divider style={{ margin: 0 }} />
		},

		...(person?.isActive
			? [
					{
						key: 'Disable Profile',
						label: (
							<DisablePeopleModal personIDs={[personID]}>
								<Button
									type='text'
									icon={<DisableIcon color='currentColor' width={16} height={16} />}
									block
									disabled={!canDisablePerson}
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
								>
									Disable Profile
								</Button>
							</DisablePeopleModal>
						)
					}
				]
			: [
					{
						key: 'Enable Profile',
						label: (
							<EnablePeopleModal personIDs={[personID]}>
								<Button
									type='text'
									icon={<EnableIcon color='currentColor' width={fontSizeLG} height={fontSizeLG} />}
									block
									disabled={!canEnablePerson}
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
								>
									Enable Profile
								</Button>
							</EnablePeopleModal>
						)
					}
				]),
		{
			key: 'Delete Profile',
			label: (
				<DeletePeopleModal personIDs={[personID]}>
					<Button
						type='text'
						danger
						icon={<DeletedFilled width={16} height={16} color='currentColor' />}
						block
						disabled={!canDeletePerson}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Delete Profile
					</Button>
				</DeletePeopleModal>
			)
		}
	];

	return (
		<Dropdown
			trigger={['click']}
			placement='bottomLeft'
			open={openDropdown}
			onOpenChange={(visible) => setOpenDropdown(visible)}
			dropdownRender={() => (
				<Flex
					style={{
						backgroundColor: colorBgContainer,
						borderRadius: borderRadiusLG,
						width: 240,
						boxShadow: boxShadow,
						padding: paddingSM,
						position: 'relative'
					}}
					vertical
					gap={paddingSM}
				>
					{items.map((item) => (
						<div key={item.key} onClick={() => setOpenDropdown(false)}>
							{item.label}
						</div>
					))}
				</Flex>
			)}
		>
			{children ? (
				children
			) : (
				<Button type={isTransparent ? 'text' : 'primary'} style={{ color: isTransparent && colorPrimaryText }}>
					{isTransparent ? 'View' : 'Manage'} Person
				</Button>
			)}
		</Dropdown>
	);
};

export default ManageLockerPersonDropdown;
